import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { CalloutContext } from '@folio/stripes-core';
import { useHistory } from 'react-router-dom';
import {
  Button,
  ConflictDetectionBanner,
  Icon,
  Loading,
  Modal,
  ModalFooter,
  MultiColumnList,
  Badge,
} from '@folio/stripes-components';
import { FaBook, FaWarehouse, FaBoxes, FaInfoCircle } from 'react-icons/fa';
import { UniversalProgressTracker } from '../../../../shared/ProgressTracking/components';

// Component for displaying colored messages
function StatusMessage({ text, type }) {
  const styles = {
    success: {
      color: '#155724',
      backgroundColor: '#d4edda',
      borderColor: '#c3e6cb',
    },
    error: {
      color: '#721c24',
      backgroundColor: '#f8d7da',
      borderColor: '#f5c6cb',
    },
    warning: {
      color: '#856404',
      backgroundColor: '#fff3cd',
      borderColor: '#ffeeba',
    },
    info: {
      color: '#0c5460',
      backgroundColor: '#d1ecf1',
      borderColor: '#bee5eb',
    },
    pending: {
      color: '#0c5460',
      backgroundColor: '#d1ecf1',
      borderColor: '#bee5eb',
    },
  };

  const style = styles[type] || styles.info;

  return (
    <div
      style={{
        padding: '10px 15px',
        borderRadius: '4px',
        border: `1px solid ${style.borderColor}`,
        backgroundColor: style.backgroundColor,
        color: style.color,
        margin: '5px 0',
        fontSize: '0.9rem',
      }}
    >
      {text}
    </div>
  );
}

StatusMessage.propTypes = {
  text: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info', 'pending'])
    .isRequired,
};



function OnSubmitModal({
  onClose,
  open,
  instanceId,
  instance,
  processCounter,
  instanceProgressMsg,
  holdingsProgressMsg,
  itemsProgressMsg,
  errorMessages,
  totalProcesses,
  startUpdate,
  setStartUpdate,
  processesSammary,
  isProcessing,
  currentBatch,
  totalBatches,
  showErrors,
  setShowErrors,
  setShowErrorModal,
  optimisticLockError,
}) {
  const context = useContext(CalloutContext);
  const history = useHistory();

  const handleCloseModal = () => {
    const message = (
      <FormattedMessage
        id="ui-plugin-inventory-templates.success.message"
        values={{ title: instance?.title || 'Instance' }}
        defaultMessage="Instance ({title}) updated successfully"
      />
    );

    // Navigate to view page and show success message
    if (instanceId) {
      history.push(`/inventory/view/${instanceId}`);
    }
    
    context.sendCallout({ message });
    onClose();
  };

  const isCompleted =
    processCounter === totalProcesses &&
    startUpdate &&
    totalProcesses > 0 &&
    !isProcessing;

  // Display operation summary before starting
  const renderSummary = () => (
    <div style={{ padding: '20px' }}>
      {/* Show optimistic locking banner if there's a conflict */}
      {optimisticLockError && (
        <div style={{ marginBottom: '20px' }}>
          <ConflictDetectionBanner
            latestVersionLink={optimisticLockError.latestVersionLink}
            focusConflictDetectionBanner={() => {}}
          />
          <div style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            color: '#721c24'
          }}
          >
            <strong>{optimisticLockError.message}</strong>
            {optimisticLockError.details && (
              <div style={{ fontSize: '0.9rem', marginTop: '5px' }}>
                {optimisticLockError.details}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <h4>
          <FormattedMessage
            id="ui-plugin-inventory-templates.createProgress.instanceTitle"
            defaultMessage="Instance Title:"
          />
        </h4>
        <div
          style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            fontWeight: 'bold',
          }}
        >
          {instance?.title || <FormattedMessage id="ui-plugin-inventory-templates.instanceTitle.untitled" defaultMessage="Untitled" />}
        </div>
      </div>

      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FormattedMessage
          id="ui-plugin-inventory-templates.summary.title"
          defaultMessage="Operation Summary"
        />
        {totalProcesses > 0 && (
          <Badge type="primary">
            {totalProcesses}
          </Badge>
        )}
      </h3>

      <p>
        <FormattedMessage
          id="ui-plugin-inventory-templates.summary.description"
          defaultMessage="The following operations will be performed:"
        />
      </p>

      {totalProcesses === 0 ? (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            color: '#6c757d',
          }}
        >
          <FaInfoCircle size={32} style={{ marginBottom: '10px', color: '#6c757d' }} />
          <div style={{ fontSize: '1.1rem', fontWeight: '500' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.summary.noChanges"
              defaultMessage="No changes detected"
            />
          </div>
          <div style={{ fontSize: '0.9rem', marginTop: '5px' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.summary.noChangesDescription"
              defaultMessage="There are no modifications to save. Make some changes to enable the update process."
            />
          </div>
        </div>
      ) : (
        <UniversalProgressTracker
          mode="edit"
          summary={processesSammary}
          showDetailedStats={true}
          showBatchInfo={false}
          showAccordion={false}
          current={0}
          total={0}
          instanceMessages={[]}
          holdingsMessages={[]}
          itemsMessages={[]}
        />
      )}

      {totalProcesses > 50 && (
        <div
          style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeeba',
            borderRadius: '4px',
          }}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.warning.large.dataset"
            values={{ total: totalProcesses }}
            defaultMessage="⚠️ Large dataset detected ({total} operations). Processing will be done in batches for stability."
          />
        </div>
      )}
    </div>
  );

  // Display progress during processing
  const renderProgress = () => (
    <div style={{ padding: '20px' }}>
      <h3>
        <FormattedMessage
          id="ui-plugin-inventory-templates.progress.title"
          defaultMessage="Processing Progress"
        />
      </h3>

      <UniversalProgressTracker
        mode="edit"
        current={processCounter}
        total={totalProcesses}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        summary={processesSammary}
        instanceMessages={instanceProgressMsg}
        holdingsMessages={holdingsProgressMsg}
        itemsMessages={itemsProgressMsg}
        showDetailedStats={false}
        showBatchInfo={true}
        showAccordion={false}
        progressColor="#007bff"
        progressHeight="20px"
        showInlineText={true}
      />

      {isProcessing && (
        <StatusMessage
          text={
            <FormattedMessage
              id="ui-plugin-inventory-templates.progress.processing"
              defaultMessage="⚡ Processing in optimized batches for better performance..."
            />
          }
          type="info"
        />
      )}
    </div>
  );

  // Control buttons
  const renderFooter = () => {
    if (!startUpdate) {
      return (
        <ModalFooter>
          <Button
            buttonStyle="primary"
            onClick={() => setStartUpdate(true)}
            disabled={optimisticLockError !== null || totalProcesses === 0}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.button.start"
              defaultMessage="Start Processing"
            />
          </Button>
          <Button onClick={onClose}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.button.cancel"
              defaultMessage="Cancel"
            />
          </Button>
        </ModalFooter>
      );
    }

    if (isProcessing && !isCompleted) {
      return (
        <ModalFooter>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <Loading size="large" />
            <div>
              <StatusMessage
                text={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.processing.message"
                    defaultMessage="Processing... Please wait, this may take several minutes."
                  />
                }
                type="pending"
              />
            </div>
          </div>
        </ModalFooter>
      );
    }

    if (isCompleted || (!isProcessing && startUpdate)) {
      return (
        <ModalFooter>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button buttonStyle="primary" onClick={handleCloseModal}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.button.finish"
                defaultMessage="Finish"
              />
            </Button>

            {errorMessages && errorMessages.length > 0 && (
              <Button
                buttonStyle="danger"
                onClick={() => setShowErrorModal(true)}
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.button.viewErrorsWithCount"
                  values={{ count: errorMessages.length }}
                />
              </Button>
            )}
          </div>

          <div style={{ marginLeft: '15px' }}>
            {errorMessages && errorMessages.length > 0 ? (
              <StatusMessage
                text={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.result.with.errors"
                    values={{ count: errorMessages.length }}
                    defaultMessage="Completed with {count} errors"
                  />
                }
                type="warning"
              />
            ) : (
              <StatusMessage
                text={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.result.success"
                    defaultMessage="All operations completed successfully!"
                  />
                }
                type="success"
              />
            )}
          </div>
        </ModalFooter>
      );
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={!isProcessing ? onClose : undefined}
        size="large"
        label={
          <FormattedMessage
            id="ui-plugin-inventory-templates.modal.title"
            values={{ instanceHrid: instance?.hrid || 'Unknown' }}
            defaultMessage="Update Progress - {instanceHrid}"
          />
        }
        footer={renderFooter()}
      >
        {!startUpdate ? renderSummary() : renderProgress()}
      </Modal>

      {/* Separate error modal */}
    </>
  );
}

OnSubmitModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  instanceId: PropTypes.string,
  instance: PropTypes.object,
  processCounter: PropTypes.number,
  instanceProgressMsg: PropTypes.array,
  holdingsProgressMsg: PropTypes.array,
  itemsProgressMsg: PropTypes.array,
  errorMessages: PropTypes.array,
  totalProcesses: PropTypes.number,
  startUpdate: PropTypes.bool,
  setStartUpdate: PropTypes.func,
  processesSammary: PropTypes.array,
  isProcessing: PropTypes.bool,
  currentBatch: PropTypes.number,
  totalBatches: PropTypes.number,
  showErrors: PropTypes.bool,
  setShowErrors: PropTypes.func,
  setShowErrorModal: PropTypes.func,
  optimisticLockError: PropTypes.object,
};

OnSubmitModal.defaultProps = {
  open: false,
  instanceId: '',
  instance: {},
  processCounter: 0,
  instanceProgressMsg: [],
  holdingsProgressMsg: [],
  itemsProgressMsg: [],
  errorMessages: [],
  totalProcesses: 0,
  startUpdate: false,
  processesSammary: [],
  isProcessing: false,
  currentBatch: 0,
  totalBatches: 0,
  showErrors: false,
  optimisticLockError: null,
};

export default OnSubmitModal;
